import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Warehouse, Search, AlertTriangle, Package, CheckCircle, Hash, FileText, X, SlidersHorizontal } from 'lucide-react';
import { useIsAdmin } from '../context/AuthContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Inventory = () => {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Selection state
  const [selectedItems, setSelectedItems] = useState({});

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory`);
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  // Derive unique filter options from inventory
  const filterOptions = useMemo(() => {
    const brands = [...new Set(inventory.map(i => i.brand).filter(Boolean))].sort();
    const products = [...new Set(inventory.map(i => i.product_name).filter(Boolean))].sort();
    const sizes = [...new Set(inventory.map(i => i.size).filter(Boolean))].sort();
    return { brands, products, sizes };
  }, [inventory]);

  const activeFilterCount = [brandFilter, productFilter, sizeFilter].filter(f => f !== 'all').length;

  // Filtering logic
  const filteredInventory = useMemo(() =>
    inventory.filter(inv => {
      const term = searchTerm.toLowerCase();
      const matchesSearch = !term ||
        inv.product_name.toLowerCase().includes(term) ||
        inv.brand.toLowerCase().includes(term) ||
        (inv.size || '').toLowerCase().includes(term) ||
        inv.batch_number.toLowerCase().includes(term);
      const matchesBrand = brandFilter === 'all' || inv.brand === brandFilter;
      const matchesProduct = productFilter === 'all' || inv.product_name === productFilter;
      const matchesSize = sizeFilter === 'all' || inv.size === sizeFilter;
      return matchesSearch && matchesBrand && matchesProduct && matchesSize;
    }), [inventory, searchTerm, brandFilter, productFilter, sizeFilter]);

  const totalStock = inventory.reduce((sum, inv) => sum + inv.quantity, 0);
  const lowStockCount = inventory.filter(inv => inv.low_stock).length;
  const uniqueProducts = [...new Set(inventory.map(inv => inv.product_id))].length;

  // Selection helpers
  const itemKey = (inv) => `${inv.product_id}|${inv.batch_number}`;
  const selectedCount = Object.keys(selectedItems).length;

  const toggleSelect = (inv) => {
    const key = itemKey(inv);
    setSelectedItems(prev => {
      if (key in prev) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: Math.min(1, inv.quantity) };
    });
  };

  const selectAllFiltered = (checked) => {
    if (checked) {
      const selectable = filteredInventory.filter(i => i.quantity > 0);
      const newSelections = { ...selectedItems };
      selectable.forEach(inv => {
        const key = itemKey(inv);
        if (!(key in newSelections)) newSelections[key] = Math.min(1, inv.quantity);
      });
      setSelectedItems(newSelections);
    } else {
      // Deselect only filtered items
      const filteredKeys = new Set(filteredInventory.map(itemKey));
      setSelectedItems(prev => {
        const next = {};
        for (const [k, v] of Object.entries(prev)) {
          if (!filteredKeys.has(k)) next[k] = v;
        }
        return next;
      });
    }
  };

  const allFilteredSelected = filteredInventory.filter(i => i.quantity > 0).length > 0 &&
    filteredInventory.filter(i => i.quantity > 0).every(i => itemKey(i) in selectedItems);

  const updateQtyToSell = (inv, qty) => {
    const key = itemKey(inv);
    const val = Math.max(1, Math.min(parseInt(qty) || 1, inv.quantity));
    setSelectedItems(prev => ({ ...prev, [key]: val }));
  };

  const handleCreateInvoice = () => {
    if (selectedCount === 0) {
      toast.error('Select at least one item');
      return;
    }
    const items = [];
    for (const [key, qty] of Object.entries(selectedItems)) {
      const [productId, batchNumber] = key.split('|');
      const inv = inventory.find(i => i.product_id === productId && i.batch_number === batchNumber);
      if (inv) {
        items.push({
          product_id: productId,
          batch_number: batchNumber,
          quantity: qty.toString(),
          selling_price: inv.selling_price || '',
          gst_percentage: inv.gst_percentage || 12
        });
      }
    }
    navigate('/sales?create=true', { state: { inventoryItems: items } });
  };

  const clearSelection = () => setSelectedItems({});

  const clearFilters = () => {
    setBrandFilter('all');
    setProductFilter('all');
    setSizeFilter('all');
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Inventory</h1>
          <p className="page-subtitle">Track your stock levels by product and batch number</p>
        </div>
        {isAdmin && selectedCount > 0 && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 px-3 py-1">
              {selectedCount} item(s) selected
            </Badge>
            <Button
              onClick={handleCreateInvoice}
              className="btn-primary"
              data-testid="create-invoice-from-inventory-btn"
            >
              <FileText className="w-4 h-4 mr-1" />
              Create Invoice
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Total Stock</p>
                <p className="kpi-value">{totalStock.toLocaleString()}</p>
                <p className="text-xs text-slate-400 mt-1">Units available</p>
              </div>
              <div className="kpi-card-icon bg-teal-100">
                <Warehouse className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Products</p>
                <p className="kpi-value">{uniqueProducts}</p>
                <p className="text-xs text-slate-400 mt-1">Unique items</p>
              </div>
              <div className="kpi-card-icon bg-blue-100">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Batches</p>
                <p className="kpi-value">{inventory.length}</p>
                <p className="text-xs text-slate-400 mt-1">Tracked batches</p>
              </div>
              <div className="kpi-card-icon bg-violet-100">
                <Hash className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`border shadow-sm ${lowStockCount > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Low Stock Alert</p>
                <p className={`kpi-value ${lowStockCount > 0 ? 'text-red-600' : ''}`}>{lowStockCount}</p>
                <p className="text-xs text-slate-400 mt-1">Below 5 units</p>
              </div>
              <div className={`kpi-card-icon ${lowStockCount > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                {lowStockCount > 0 ? <AlertTriangle className="w-6 h-6 text-red-600" /> : <CheckCircle className="w-6 h-6 text-emerald-600" />}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search + Filters */}
      <Card className="border border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by product, brand, size, or batch..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="inventory-search-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={cn(showFilters && 'bg-slate-100')}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4 mr-1" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-xs h-5 px-1.5 bg-teal-100 text-teal-700">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-slate-100">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Brand</Label>
                <Select value={brandFilter} onValueChange={setBrandFilter}>
                  <SelectTrigger className="w-44 h-8 text-sm" data-testid="brand-filter">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {filterOptions.brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Product Name</Label>
                <Select value={productFilter} onValueChange={setProductFilter}>
                  <SelectTrigger className="w-52 h-8 text-sm" data-testid="product-filter">
                    <SelectValue placeholder="All Products" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {filterOptions.products.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Size</Label>
                <Select value={sizeFilter} onValueChange={setSizeFilter}>
                  <SelectTrigger className="w-44 h-8 text-sm" data-testid="size-filter">
                    <SelectValue placeholder="All Sizes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sizes</SelectItem>
                    {filterOptions.sizes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500 h-8">
                  Clear all
                </Button>
              )}
              <span className="text-xs text-slate-400 ml-auto self-center">
                {filteredInventory.length} of {inventory.length} items
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-slate-200">
        <CardHeader className="border-b border-slate-200 py-4">
          <CardTitle className="text-base font-semibold text-slate-900">
            Inventory by Batch {isAdmin && <span className="text-xs font-normal text-slate-400 ml-2">Select items to create an invoice</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredInventory.length === 0 ? (
            <div className="empty-state py-16">
              <Warehouse className="empty-state-icon" />
              <p className="text-slate-500 font-medium">No inventory found</p>
              <p className="text-sm text-slate-400 mt-1">
                {activeFilterCount > 0 ? 'Try adjusting your filters' : 'Add purchases to build your inventory'}
              </p>
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" className="mt-3" onClick={clearFilters}>Clear Filters</Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="professional-table">
                <TableHeader>
                  <TableRow>
                    {isAdmin && (
                      <TableHead className="w-12">
                        <Checkbox
                          checked={allFilteredSelected}
                          onCheckedChange={selectAllFiltered}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                    )}
                    <TableHead>Product</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Batch Number</TableHead>
                    <TableHead className="text-right">Available Qty</TableHead>
                    {isAdmin && <TableHead className="text-right w-28">Sell Qty</TableHead>}
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInventory.map((inv, index) => {
                    const key = itemKey(inv);
                    const selected = key in selectedItems;
                    return (
                      <TableRow
                        key={`${inv.product_id}-${inv.batch_number}`}
                        className={cn(
                          inv.low_stock && 'bg-red-50/50',
                          selected && 'bg-teal-50/60 border-l-2 border-l-teal-400'
                        )}
                        data-testid={`inventory-row-${index}`}
                      >
                        {isAdmin && (
                          <TableCell>
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() => toggleSelect(inv)}
                              disabled={inv.quantity === 0}
                              data-testid={`inv-select-${index}`}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${inv.low_stock ? 'bg-red-100' : 'bg-slate-100'}`}>
                              <Package className={`w-4 h-4 ${inv.low_stock ? 'text-red-500' : 'text-slate-500'}`} />
                            </div>
                            <span className="font-medium text-slate-900">{inv.product_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-700">{inv.brand}</TableCell>
                        <TableCell>
                          {inv.size ? (
                            <span className="text-sm text-slate-700">{inv.size}</span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-slate-100 rounded text-sm font-mono text-slate-700">
                            {inv.batch_number}
                          </code>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`text-lg tabular-nums font-bold ${inv.low_stock ? 'text-red-600' : 'text-slate-900'}`}>
                            {inv.quantity}
                          </span>
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            {selected ? (
                              <Input
                                type="number"
                                min="1"
                                max={inv.quantity}
                                value={selectedItems[key]}
                                onChange={(e) => updateQtyToSell(inv, e.target.value)}
                                className="w-20 h-8 text-sm text-right ml-auto"
                                data-testid={`inv-qty-${index}`}
                              />
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          {inv.low_stock ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                              <AlertTriangle className="w-3 h-3" />
                              Low Stock
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                              <CheckCircle className="w-3 h-3" />
                              In Stock
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch Traceability Info */}
      <Card className="border border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Hash className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-800 mb-1">Batch Number Traceability</h4>
              <p className="text-sm text-blue-700">
                Each inventory item is tracked by batch number for medical device traceability.
                This helps identify which implant batches were sold to which customers,
                ensuring compliance with medical device regulations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Inventory;
